import { ChevronsUpDown, ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { SortDirection as SortDirectionEnum } from '@/enums/sort-direction.enum'
import { ToolSortColumn } from '@/enums/tool-sort-column.enum'
import { ImagePreview } from '@/components/common/ImagePreview'
import { cn } from '@/lib/utils'
import type { Tool } from '@/hooks/useToolManagement/useGetTools'
import { formatDate } from '@/utils/format-date.util'
import { getEarliestCreatedAt } from '@/utils/tool-management/tool-management-table/get-earliest-created-at.util'

type SortColumn = Nullable<ToolSortColumn>
type SortDirection = Nullable<SortDirectionEnum>

interface ToolTableProps {
  tools: Tool[]
  sortColumn: SortColumn
  sortDirection: SortDirection
  onSort: (column: SortColumn) => void
}

function getSortIcon(
  column: SortColumn,
  currentSortColumn: SortColumn,
  sortDirection: SortDirection
) {
  if (currentSortColumn !== column) {
    return <ChevronsUpDown className='h-3 w-3 text-gray-400' />
  }
  if (sortDirection === SortDirectionEnum.Asc) {
    return <ChevronUp className='h-3 w-3 text-gray-600' />
  }
  if (sortDirection === SortDirectionEnum.Desc) {
    return <ChevronDown className='h-3 w-3 text-gray-600' />
  }
  return <ChevronsUpDown className='h-3 w-3 text-gray-400' />
}

export function ToolTable({
  tools,
  sortColumn,
  sortDirection,
  onSort,
}: ToolTableProps) {
  const { t } = useTranslation()
  const earliestCreatedAt = getEarliestCreatedAt(tools)

  return (
    <div>
      <table className='w-full text-sm overflow-x-auto border rounded-lg'>
        <thead className='border-b'>
          <tr>
            <th
              colSpan={4}
              className='px-4 py-3 text-center text-sm font-bold text-[#181D27] uppercase bg-gray-50'
            >
              {t('toolManagement.information')}
            </th>
            <th
              colSpan={3}
              className='px-4 py-3 text-center text-sm font-bold text-[#181D27] uppercase border-l bg-gray-50'
            >
              {t('common.status')}
            </th>
            <th
              colSpan={1}
              className='px-4 py-3 text-center text-sm font-bold text-gray-800 uppercase border-l bg-gray-50'
            ></th>
          </tr>
          <tr>
            <th className='px-4 py-3 text-left font-medium text-[#717680]'>
              {t('toolManagement.pn')}
            </th>
            <th className='px-4 py-3 text-left font-medium text-[#717680]'>
              {t('toolManagement.chineseName')}
            </th>
            <th className='px-4 py-3 text-left font-medium text-[#717680]'>
              <div
                className='flex items-center gap-2 cursor-pointer hover:text-gray-900 select-none'
                onClick={() => onSort(ToolSortColumn.Description)}
              >
                {t('toolManagement.description')}
                {getSortIcon(
                  ToolSortColumn.Description,
                  sortColumn,
                  sortDirection
                )}
              </div>
            </th>
            <th className='px-4 py-3 text-center font-medium text-[#717680]'>
              <div
                className='flex items-center justify-center gap-2 cursor-pointer hover:text-gray-900 select-none'
                onClick={() => onSort(ToolSortColumn.Quantity)}
              >
                {t('toolManagement.quantity')}
                {getSortIcon(
                  ToolSortColumn.Quantity,
                  sortColumn,
                  sortDirection
                )}
              </div>
            </th>
            <th className='px-4 py-3 text-center font-medium text-green-600 border-l'>
              {t('toolManagement.available')}
            </th>
            <th className='px-4 py-3 text-center font-medium text-orange-600'>
              {t('toolManagement.borrowed')}
            </th>
            <th className='px-4 py-3 text-center font-medium text-red-600'>
              {t('toolManagement.missing')}
            </th>
            <th className='px-4 py-3 text-center font-medium text-[#717680] border-l'>
              {t('toolManagement.image')}
            </th>
          </tr>
        </thead>
        <tbody>
          {tools.length === 0 ? (
            <tr>
              <td
                colSpan={8}
                className='px-4 py-8 text-center text-gray-500 text-sm'
              >
                {t('toolManagement.noToolsFound')}
              </td>
            </tr>
          ) : (
            tools.map((tool: Tool, index: number) => (
              <tr
                key={tool.id ?? index}
                className={cn(index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}
              >
                <td className='px-4 py-3 text-gray-900'>
                  {tool.part_number || '-'}
                </td>
                <td className='px-4 py-3 text-gray-900 font-bold'>
                  {tool.chinese_name || '-'}
                </td>
                <td className='px-4 py-3 text-gray-600'>
                  {tool.description || '-'}
                </td>
                <td className='px-4 py-3 text-center text-gray-900'>
                  {tool.quantity || 0}
                </td>
                <td className='px-4 py-3 text-center border-l'>
                  <span className='inline-block px-2 py-1 text-xs text-green-700 bg-green-100 rounded-md'>
                    {tool.available_quantity || 0}
                  </span>
                </td>
                <td className='px-4 py-3 text-center'>
                  <span className='inline-block px-2 py-1 text-xs text-orange-700 bg-orange-100 rounded-md'>
                    {tool.borrowed_quantity || 0}
                  </span>
                </td>
                <td className='px-4 py-3 text-center'>
                  <span className='inline-block px-2 py-1 text-xs text-red-700 bg-red-100 rounded-md'>
                    {tool.missing_quantity || 0}
                  </span>
                </td>
                <td className='px-4 py-3 border-l text-center'>
                  <div className='flex justify-center'>
                    <ImagePreview
                      url={tool.image_url}
                      name={tool.part_number}
                      width={40}
                      height={40}
                      emptyPlaceholder='minus'
                    />
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {earliestCreatedAt && (
        <p className='text-sm text-gray-600 mt-4 text-left'>
          {t('toolManagement.createdOn')}{' '}
          <span className='font-bold'>{formatDate(earliestCreatedAt)}</span>
        </p>
      )}
    </div>
  )
}
